import maplibregl from 'maplibre-gl'

export type SimpleSectorDb = Record<string, SimpleNation>

type Point = { x: number; y: number }

export type SimpleCanton = {
    name: string
    id: string
    region: string
    nation: string
    bounding_box: BoundingBox
    center?: Point
}

type SimpleRegion = {
    name: string
    id: string
    cantons: Record<string, SimpleCanton>
    nation: string
    bounding_box: BoundingBox
    center?: Point
}

type SimpleNation = {
    name: string
    id: string
    regions: Record<string, SimpleRegion>
    bounding_box: BoundingBox
    center?: Point
}

export type Sector = {
    region: SimpleRegion
    canton: SimpleCanton
}

type BoundingBox = {
    /** min `lat` */
    a: number
    /** min `lng` */
    b: number
    /** max `lat` */
    c: number
    /** max `lng` */
    d: number
}

export type LandmarkLocation = {
    bbox?: BoundingBox
    center?: Point
}
// ({ kind: 'bbox' } & BoundingBox) | { kind: 'point'; x: number; y: number }

type LandmarkParent = {
    nation?: string
    region?: string
    canton?: string
}

export type Landmark = LandmarkLocation & {
    parent: LandmarkParent
    kind: LandmarkKind
}
/**  `name`: `Landmark`[]  */
export type LandmarkDb = Record<string, Landmark[]>

type LandmarkKind =
    | 'city'
    | 'town'
    | 'suburb'
    | 'neighbourhood'
    | 'road_primary'
    | 'road_secondary'

export function bboxCenter(bbox: BoundingBox): [number, number] {
    return [
        (bbox.b + bbox.d) / 2, // lng
        (bbox.a + bbox.c) / 2, // lat
    ]
}

export function bboxToLngLatBounds(
    bbox: BoundingBox
): [[number, number], [number, number]] {
    return [
        [bbox.b, bbox.a], // SW
        [bbox.d, bbox.c], // NE
    ]
}

export const LngLatBoundsToBbox = (bbox: BoundingBox) => {
    const bounds = new maplibregl.LngLatBounds(
        [bbox.b, bbox.a],
        [bbox.d, bbox.c]
    )

    const center = bounds.getCenter()

    return {
        lat: center.lat,
        lng: center.lng,
        radius: Math.max(
            1,
            Math.round(center.distanceTo(bounds.getNorthEast())) / 1e3
        ),
    }
}

export function isPointInsideBbox(
    lat: number,
    lng: number,
    bbox: BoundingBox
): boolean {
    return lat >= bbox.a && lat <= bbox.c && lng >= bbox.b && lng <= bbox.d
}

export const QUICK_CITIES = [
    {
        title: 'تهران',
        region: 'tehran',
        canton: 'tehran',
    },
    {
        title: 'رشت',
        region: 'gilan',
        canton: 'rasht',
    },
    {
        title: 'مشهد',
        region: 'razavi_khorasan',
        canton: 'mashhad',
    },
    {
        title: 'قم',
        region: 'qom',
        canton: 'qom',
    },
    {
        title: 'شیراز',
        region: 'fars',
        canton: 'shiraz',
    },
] as const

export function findRegion(
    db: SimpleSectorDb,
    regionId: string
): SimpleRegion | null {
    for (const nation of Object.values(db)) {
        const region = nation.regions[regionId]

        if (region) {
            return region
        }
    }

    return null
}

export function findSector(
    db: SimpleSectorDb,
    regionId: string,
    cantonId: string
): Sector | null {
    const region = findRegion(db, regionId)

    if (!region) return null

    const canton = region.cantons[cantonId]

    if (!canton) return null

    return {
        region,
        canton,
    }
}

let sectorDbPromise: Promise<SimpleSectorDb> | null = null

export const loadSectorDb = () => {
    if (!sectorDbPromise) {
        sectorDbPromise = import('shared/simple-sector-db').then(
            m => m.SECTOR_MAIN_DB
        )
    }

    return sectorDbPromise
}

export const LARGE_CITY_CANTONS = new Set([
    'tehran',
    'mashhad',
    'isfahan',
    'shiraz',
    'tabriz',
    'karaj',
    'ahvaz',
    'qom',
    'kermanshah',
    'urmia',
    'rasht',
    'hamadan',
    'yazd',
    'ardabil',
    'bandar-abbas',
])

export const getLandmarkKindName = (kind: Landmark['kind']) => {
    if (kind === 'city' || kind === 'town') {
        return 'شهر'
    }

    if (kind === 'neighbourhood') {
        return 'محله'
    }

    return ''
}

export const LANDMARK_RADIUS_KM: Record<Landmark['kind'], number> = {
    city: 15,
    town: 7,
    suburb: 4,
    neighbourhood: 1.5,
    road_primary: 5,
    road_secondary: 2.5,
}
